import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouterState, push } from 'connected-react-router';

import {
  PurchaseInformation,
  getPurchasesHistory,
  getContractedPlan,
  reset,
  StateType,
  SortType,
  GET_PURCHASE_HISTORY_ID,
  PurchaseInitialState
} from 'common/modules/domain/purchase';
import {
  getTerminalsActivation,
  resetTerminalsActivation
} from 'common/modules/domain/terminal';
import {
  getDevicesActivation,
  resetDevicesActivation
} from 'common/modules/domain/deviceActivate';
import { getLendingUserList } from 'common/modules/domain/lendingUser';
import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { UserInformation, getLogin } from 'common/modules/domain/user';
import { Principal } from 'common/modules/app/principal';
import { GlossaryState } from 'common/modules/app/glossaries';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import {
  boardState,
  setStatus,
  setPurchaseIdForCancel,
  setPurchaseIdForTerminal,
  setPurchaseIdForReturn,
  BoardStatus,
  setQuantity,
  setIsSelloutPlan,
  setPlanName,
  setDeleteIndex,
  setCancelIndex,
  setIsDemo,
  setCheckIndex
} from 'common/modules/app/board';
import { Glossary } from 'common/utils/glossary';
import { createQuery, SasPagePath } from 'common/utils/urls';
import { AppState } from 'pages';
import { ContractPlanLists } from 'pages/purchase/components/ContractPlanLists';

/** PlanをStateからPropsに変換する */
const _convertPlans = (
  contractPlans: PurchaseInformation[],
  onClickDetail: (planId: string) => () => void,
  onClickCancel: (planId: string) => () => void,
  onClickDevice: (planId: string) => () => void,
  onClickActivate: (planId: string) => () => void,
  onClickCancellationOfUse: (purchaseId: string) => () => void,
  onClickLendingUser: (
    purchaseQuantity: number | null,
    purchaseId: string,
    contractPlanName: string,
    expirationDatetime: string | null,
    perpetualValidityPeriod: number | null,
    state: string
  ) => () => void
) => {
  return contractPlans.map((p: PurchaseInformation) => ({
    purchaseId: p.purchaseId,
    imageSrc: p.plan.imageUrl,
    contractPlanId: p.plan.planId,
    contractPlanName: p.plan.name,
    operationDatetime: p.operationDatetime,
    expirationDatetime: p.expirationDatetime,
    expirationGraceDays: p.expirationGraceDays,
    contractTaxIncludedPrice: p.plan.price.taxIncludedPrice,
    vendorPurchaseId: p.vendorPurchaseId,
    validityPeriod: p.plan.validityPeriod,
    perpetualValidityPeriod: p.plan.perpetualValidityPeriod,
    usage: p.services[0].usage,
    limit: p.services[0].limit,
    currency: p.plan.price.currency,
    isDemo: p.state == StateType.TYPE_DEMO,
    isCanceled: p.state == StateType.TYPE_CANCEL,
    state: p.state,
    planLimit: p.planLimit,
    planUsage: p.planUsage,
    lendingQuantity: p.lendingQuantity,
    purchaseQuantity: p.quantity,
    lendingStatus: p.lendingStatus,
    deviceLimitType: p.plan.deviceLimitType,
    lendingType: p.plan.lendingType,
    renewalType: p.renewalType,
    onClickDetail: onClickDetail(p.plan.planId),
    onClickCancel: onClickCancel(p.purchaseId),
    onClickDevice: onClickDevice(p.purchaseId),
    onClickActivate: onClickActivate(p.purchaseId),
    onClickCancellationOfUse: onClickCancellationOfUse(p.purchaseId),
    onClickLendingUser: onClickLendingUser(
      p.quantity,
      p.purchaseId,
      p.plan.name,
      p.expirationDatetime,
      p.plan.perpetualValidityPeriod,
      p.state
    )
  }));
};

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const purchaseState = useSelector<AppState, PurchaseInitialState>(
    (state) => state.domain.purchase
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const principalState = useSelector<AppState, Principal>(
    (state) => state.app.principal
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );

  const isPurchaseProcessing =
    fetchState[GET_PURCHASE_HISTORY_ID] !== null &&
    fetchState[GET_PURCHASE_HISTORY_ID] === FetchStatus.PROCESSING;
  // 購入履歴画面側で取得を開始している可能性があるので、その場合は何もしない
  const isPurchaseFetched =
    fetchState[GET_PURCHASE_HISTORY_ID] !== null &&
    (fetchState[GET_PURCHASE_HISTORY_ID] === FetchStatus.UNTREATED ||
      fetchState[GET_PURCHASE_HISTORY_ID] === FetchStatus.PROCESSING);
  const contractPlans = getContractedPlan(
    purchaseState.purchasePlanList,
    false
  );

  return {
    contractPlans: contractPlans,
    principal: principalState,
    user: userState,
    router: routerState,
    glossary: Glossary.instance(glState),
    isFetched: isPurchaseFetched,
    isProcessing: isPurchaseProcessing
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const cookie = loadCookie();
  const _token = cookie[SasCookieKeys.KEY_TOKEN];
  const _refreshToken = cookie[SasCookieKeys.KEY_REFRESH_TOKEN];
  const isLoggedIn = state.principal.isLoggedIn;
  const _language = state.user.language;
  const _from = '';

  /** 契約中のプラン一覧画面の初期化処理 */
  const onInit = async () => {
    // 未ログインだったらログイン画面へ遷移する
    if (!isLoggedIn) {
      dispatch(getLogin());
    } else {
      // 購入履歴情報が未取得のときのみ取得する
      if (!state.isFetched) {
        await dispatch(reset());
        await dispatch(
          getPurchasesHistory(
            _token,
            _language,
            StateType.TYPE_ALL,
            SortType.TYPE_PURCHASE_ID,
            _from
          )
        );
      }
    }
  };

  /** 詳細をみるクリック時の処理 */
  const onClickDetail = (planId: string) => () => {
    const _query = createQuery([{ key: 'planId', value: planId }]);
    dispatch(push(`${SasPagePath.PlanDetail}${_query}`));
  };

  /** 「他ユーザー管理/解約手続」ボタンクリック時の処理 */
  const onClickLendingUser = (
    purchaseQuantity: any,
    purchaseId: string,
    contractPlanName: string,
    expirationDatetime: string | null,
    perpetualValidityPeriod: number | null,
    state: string
  ) => () => {
    // 他ユーザーの削除、プランの解約対象のインデックスが格納されているリストをクリアする
    dispatch(setDeleteIndex([]));
    dispatch(setCancelIndex([]));
    dispatch(setCheckIndex([]));
    // 第三者の利用登録情報を取得する
    // (内部で第三者の利用登録情報クリア及びユーザー購入番号の設定を行う)
    dispatch(getLendingUserList(_token, _refreshToken, purchaseId, true));
    // 貸与ユーザー一覧画面を表示
    dispatch(setStatus(BoardStatus.LENDING_USER_DISP));
    // 対象の購入数量、プラン名を設定する
    // (ユーザー購入番号の設定はgetLendingUserList内で行うためここでは行わない)
    dispatch(setQuantity(purchaseQuantity));
    dispatch(setPlanName(contractPlanName));
    // 売り切りプランかどうか判別して設定する
    const isSelloutPlan =
      (!expirationDatetime && !perpetualValidityPeriod) ||
      (expirationDatetime != null && perpetualValidityPeriod != null);
    const isDemo = state == 'demo';
    dispatch(setIsSelloutPlan(isSelloutPlan));
    dispatch(setIsDemo(isDemo));
  };

  /** 解約ボタンクリック時の処理 */
  const onClickCancel = (purchaseId: string) => () => {
    // 解約確認画面を表示
    dispatch(setPurchaseIdForCancel(purchaseId));
    dispatch(setStatus(BoardStatus.BEFORE_CANCEL_DISP));
  };

  /** デバイス管理ボタンクリック時の処理*/
  const onClickDevice = (purchaseId: string) => () => {
    // 最初に取得した端末情報一覧をクリア
    dispatch(resetTerminalsActivation());
    // 端末情報一覧取得APIを実行
    dispatch(getTerminalsActivation(_token, purchaseId));
    // 選択したユーザー購入番号をStateに保持
    dispatch(setPurchaseIdForTerminal(purchaseId));
    // 端末管理画面を表示
    dispatch(setStatus(BoardStatus.TERMINAL_DISP));
  };

  /** 利用者の利用中画面 利用解除ボタンクリック時の処理 */
  const onClickCancellationOfUse = (purchaseId: string) => () => {
    // 選択したユーザー購入番号をStateに保持
    dispatch(setPurchaseIdForReturn(purchaseId));
    // 利用解除確認ダイアログを表示
    dispatch(setStatus(BoardStatus.CANCELLATION_CONFIRMATION_DISP));
  };

  /** 機能のアクティベートボタンクリック時の処理*/
  const onClickActivate = (purchaseId: string) => () => {
    // 最初に取得した有償機能アクティベート情報一覧をクリア
    dispatch(resetDevicesActivation());
    // 有償機能アクティベート情報取得APIを実行
    dispatch(
      getDevicesActivation(_token, _refreshToken, purchaseId, false, false)
    );
    // 選択したユーザー購入番号をStateに保持
    dispatch(setPurchaseIdForTerminal(purchaseId));
    // 機能のアクティベート管理画面を表示
    dispatch(setStatus(BoardStatus.DEVICE_ACTIVATE_DISP));
  };

  return {
    contractPlans: _convertPlans(
      state.contractPlans,
      onClickDetail,
      onClickCancel,
      onClickDevice,
      onClickActivate,
      onClickCancellationOfUse,
      onClickLendingUser
    ),
    initializePage: onInit,
    glossary: state.glossary,
    language: state.user.language,
    isLoggedIn: isLoggedIn,
    isProcessing: state.isProcessing
  };
};

/**
 * 契約中プランリストのContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useMargeProps(useStateProps()) };
  return <ContractPlanLists {..._props} />;
}
